import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ProgressBanner from '../../components/profile/learningProgress/ProgressBanner';
import ProgressOverviewSection from '../../components/profile/learningProgress/ProgressOverviewSection';
import SubjectProgressList from '../../components/profile/learningProgress/SubjectProgressList';
import { useAuthContext } from '../../context/AuthContext';
import { useProfileContext } from '../../context/ProfileContext';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { useCategories } from '../../hooks/useCategories';
import { useLearningProgress } from '../../hooks/useLearningProgress';
import { mapLearningProgressData } from '../../utils/learningProgressMappers';
import { mapProfileToForm } from '../../utils/profileMappers';

const SUBSCRIPTION_TOAST = {
  message: 'Get an active subscription for this class to view your learning progress.',
  type: 'info',
};

export default function ProfileProgressPage() {
  const { isLoggedIn } = useAuthContext();
  const { user: layoutUser } = useOutletContext() || {};
  const { user: contextUser } = useProfileContext();
  const { isCategorySubscribed, loading: subscriptionsLoading } = useSubscriptionContext();
  const user = layoutUser || contextUser;
  const userName = mapProfileToForm(user)?.fullName || 'Student';

  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories(isLoggedIn);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [performancePeriod, setPerformancePeriod] = useState('this_week');
  const [toast, setToast] = useState(null);

  const showSubscriptionToast = useCallback(() => {
    setToast(SUBSCRIPTION_TOAST);
  }, []);

  useEffect(() => {
    if (!categories.length || subscriptionsLoading) return;

    setSelectedCategoryId((current) => {
      const subscribedCategory = categories.find((category) => isCategorySubscribed(category.id));

      if (subscribedCategory) {
        if (current && isCategorySubscribed(current)) return current;
        return subscribedCategory.id;
      }

      return current ?? categories[0]?.id ?? null;
    });
  }, [categories, subscriptionsLoading, isCategorySubscribed]);

  const canLoadProgress = useMemo(
    () => isLoggedIn && Boolean(selectedCategoryId) && isCategorySubscribed(selectedCategoryId),
    [isLoggedIn, selectedCategoryId, isCategorySubscribed]
  );

  const {
    data: progressData,
    loading: progressLoading,
    error: progressError,
    subscriptionRequired,
  } = useLearningProgress(selectedCategoryId, performancePeriod, canLoadProgress);

  const mappedProgress = useMemo(
    () => mapLearningProgressData(progressData, performancePeriod),
    [progressData, performancePeriod]
  );

  const pageLoading =
    categoriesLoading || subscriptionsLoading || (canLoadProgress && progressLoading);
  const pageError = categoriesError || progressError;

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (!isCategorySubscribed(categoryId)) {
        showSubscriptionToast();
        return;
      }

      setSelectedCategoryId(categoryId);
    },
    [isCategorySubscribed, showSubscriptionToast]
  );

  useEffect(() => {
    if (categoriesLoading || subscriptionsLoading || !selectedCategoryId) return undefined;

    if (!isCategorySubscribed(selectedCategoryId) || subscriptionRequired) {
      showSubscriptionToast();
    }
  }, [
    categoriesLoading,
    subscriptionsLoading,
    selectedCategoryId,
    isCategorySubscribed,
    subscriptionRequired,
    showSubscriptionToast,
  ]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <>
      {toast && (
        <div
          role="status"
          className="fixed left-1/2 top-6 z-[60] max-w-md -translate-x-1/2 rounded-lg bg-[#9369CC] px-4 py-3 text-sm font-medium text-white shadow-lg"
        >
          {toast.message}
        </div>
      )}

      <main className="min-w-0 space-y-6 pb-10 sm:space-y-8">
        <ProgressBanner userName={userName} />

        {pageError ? <p className="text-sm text-red-600">{pageError}</p> : null}

        <ProgressOverviewSection
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onCategoryChange={handleCategoryChange}
          isCategorySubscribed={isCategorySubscribed}
          performancePeriod={performancePeriod}
          onPerformancePeriodChange={setPerformancePeriod}
          overview={mappedProgress.overview}
          loading={pageLoading}
          progressDisabled={!canLoadProgress}
        />

        <SubjectProgressList
          subjects={canLoadProgress ? mappedProgress.subjects : []}
          loading={pageLoading && canLoadProgress}
        />
      </main>
    </>
  );
}
